/**
 * 請求書(2026-10-23・Gate 収入側 段階3・/invoices)。売上台帳 → 請求書 → 入金予定 → 入金の記録。
 * 計算は invoice.ts、読み書きは invoiceRepo.ts。
 *  - 対象月はトップバーの月。請求書は月単位(第1版)
 *  - 作る・入金を記録・取り消す: 経理・管理者 / 見る: 承認者も
 *  - 売上は計上済み。請求書では売上を増やさない(入金予定=売掛を作るだけ)
 * PC は表、スマホ(768px以下)は1件1行のリスト(横スクロールなし)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { formatAmountInput, formatYen, parseAmount } from '@core/lib/money';
import { addDays, todayStr, toMonthLabel, toShortDate } from '@core/lib/date';
import { useIsMobile } from '@core/lib/useIsMobile';
import type { CashEntry, Invoice, Transaction, Vendor } from '@core/lib/types';
import { fetchVendors } from '@core/masters/vendorRepo';
import { isReceivable } from '@core/partners/terms';
import {
  INVOICE_STATUS_LABELS,
  balanceOf,
  buildInvoiceDraft,
  issuerOf,
  validateDraft,
  lineOf,
  summarizeReceivables,
  taxIncluded,
  unbilledSales,
} from './invoice';
import {
  createInvoice,
  fetchCashEntriesSince,
  fetchInvoicesDueSince,
  fetchInvoicesForMonth,
  fetchOpenInvoices,
  fetchSalesForMonth,
  recordReceipt,
  updateDueDate,
  voidInvoice,
} from './invoiceRepo';
import { availableDeposits, proposeMatches, usedCashEntryIds, type MatchProposal } from './receiptMatching';

type View = 'month' | 'open' | 'unbilled' | 'match';

/** 入金の照合で見る期間(今日から何日前までの入金) */
const MATCH_LOOKBACK_DAYS = 120;

type CreateState = {
  companyId: string;
  customerId: string;
  issueDate: string;
  dueDate: string;
  note: string;
  excluded: Set<string>;
};

export default function InvoicesPage() {
  const { user, isAdmin, flowRole, canConfirm } = useAuth();
  const { accounts, companies, defaultCompanyId, accountName, departmentName } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();
  const mobile = useIsMobile();
  const canEdit = isAdmin || canConfirm || flowRole === 'accounting';

  const [view, setView] = useState<View>('month');
  const [monthInvoices, setMonthInvoices] = useState<Invoice[]>([]);
  const [openInvoices, setOpenInvoices] = useState<Invoice[]>([]);
  const [sales, setSales] = useState<Transaction[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [create, setCreate] = useState<CreateState | null>(null);
  const [receipt, setReceipt] = useState<{ invoice: Invoice; date: string; amountText: string; note: string; refund: boolean } | null>(null);
  const [dueEdit, setDueEdit] = useState<{ invoice: Invoice; date: string } | null>(null);
  const [voiding, setVoiding] = useState<{ invoice: Invoice; reason: string } | null>(null);
  // 入金の照合(銀行の入金 → 請求書)。入出金を読めるのは「確定」の権限を持つ人だけ
  const [deposits, setDeposits] = useState<CashEntry[] | null>(null);
  const [dueInvoices, setDueInvoices] = useState<Invoice[]>([]);
  const today = todayStr();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, o, s, v] = await Promise.all([
        fetchInvoicesForMonth(month),
        fetchOpenInvoices(),
        fetchSalesForMonth(month),
        fetchVendors(true).catch(() => [] as Vendor[]),
      ]);
      setMonthInvoices(m);
      setOpenInvoices(o);
      setSales(s);
      setVendors(v);
    } catch (e) {
      toast(e instanceof Error ? e.message : '請求書を読み込めませんでした');
    } finally {
      setLoading(false);
    }
  }, [month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const vendorById = useMemo(() => new Map(vendors.map((v) => [v.id, v])), [vendors]);
  const receivables = useMemo(() => summarizeReceivables(openInvoices, today), [openInvoices, today]);
  // 略号が空の会社は発行の前に止める(番号が「-202610-001」にならないように)
  const companyCode = (id: string) => companies.find((c) => c.id === id)?.code ?? '';

  /** 未請求の売上(取引先ごと)。取引先の無い売上は null にまとめる */
  const unbilledByCustomer = useMemo(() => {
    const list = unbilledSales({ transactions: sales, accounts, invoices: monthInvoices, month });
    const map = new Map<string | null, Transaction[]>();
    for (const t of list) {
      const k = t.vendor_id ?? null;
      map.set(k, [...(map.get(k) ?? []), t]);
    }
    return [...map.entries()].sort(([a], [b]) => (a === null ? 1 : b === null ? -1 : 0));
  }, [sales, accounts, monthInvoices, month]);

  /** 請求先の候補 = 使用中の売上先・元請け + この月に未請求の売上がある取引先(使用停止・区分なしでも請求できるように) */
  const customers = useMemo(() => {
    const withSales = new Set(unbilledByCustomer.map(([id]) => id).filter((id): id is string => id !== null));
    return vendors.filter((v) => (v.active && isReceivable(v)) || withSales.has(v.id));
  }, [vendors, unbilledByCustomer]);

  const describe = (t: Transaction) => t.description || `${departmentName(t.department_id)} ${accountName(t.account_id)}`;

  // --- 作成 ---
  const createLines = useMemo(() => {
    if (!create?.customerId) return [];
    // 発行する会社の売上だけ(別の会社の売上を混ぜない)
    return unbilledSales({ transactions: sales, accounts, invoices: monthInvoices, customerId: create.customerId, companyId: create.companyId, month });
  }, [create?.customerId, create?.companyId, sales, accounts, monthInvoices, month]);
  const chosen = createLines.filter((t) => !create?.excluded.has(t.id));
  // 下書きは毎回作り直す(選んだ明細が変わるたびに合計・消費税・入金予定日を出し直すため)
  const draftCustomer = create?.customerId ? vendorById.get(create.customerId) : undefined;
  const draft =
    create && draftCustomer
      ? buildInvoiceDraft({
          companyId: create.companyId,
          customer: draftCustomer,
          month,
          issueDate: create.issueDate,
          lines: chosen.map((t) => lineOf(t, describe)),
          note: create.note,
          dueDate: create.dueDate || null,
          issuer: (() => {
            const c = companies.find((x) => x.id === create.companyId);
            return c ? issuerOf(c) : null;
          })(),
        })
      : null;

  function openCreate(customerId = '') {
    // 取引先から開いたときは、その取引先の未請求の売上が一番多い会社を初めに選ぶ
    const ts = unbilledByCustomer.find(([id]) => id === customerId)?.[1] ?? [];
    const count = new Map<string, number>();
    for (const t of ts) count.set(t.company_id, (count.get(t.company_id) ?? 0) + 1);
    const companyId = [...count.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? defaultCompanyId;
    setCreate({ companyId, customerId, issueDate: today, dueDate: '', note: '', excluded: new Set() });
  }

  async function doCreate() {
    if (!user || !create || !draft) return;
    const invalid = validateDraft(draft, companyCode(create.companyId));
    if (invalid) {
      toast(invalid);
      return;
    }
    setBusy(true);
    try {
      const r = await createInvoice({ draft, companyCode: companyCode(create.companyId), userId: user.id });
      toast(`請求書 ${r.number} を発行しました`);
      setCreate(null);
      setView('month');
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '発行に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function doReceipt() {
    if (!user || !receipt) return;
    const amount = parseAmount(receipt.amountText);
    if (amount === null) {
      toast('金額を正しく入れてください');
      return;
    }
    setBusy(true);
    try {
      // 返金はマイナスで記録する(スマホの数字キーボードにマイナスが無いため、チェックで選ぶ)
      const signed = receipt.refund ? -amount : amount;
      const status = await recordReceipt({ invoiceId: receipt.invoice.id, date: receipt.date, amount: signed, note: receipt.note, userId: user.id });
      toast(`入金を記録しました(${INVOICE_STATUS_LABELS[status]})`);
      setReceipt(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '記録に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  async function doDueEdit() {
    if (!user || !dueEdit) return;
    setBusy(true);
    try {
      await updateDueDate({ invoiceId: dueEdit.invoice.id, dueDate: dueEdit.date, userId: user.id });
      toast('入金予定日を直しました');
      setDueEdit(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '直せませんでした');
    } finally {
      setBusy(false);
    }
  }

  async function doVoid() {
    if (!user || !voiding) return;
    setBusy(true);
    try {
      await voidInvoice({ invoiceId: voiding.invoice.id, reason: voiding.reason, userId: user.id });
      toast(`請求書 ${voiding.invoice.number} を取り消しました`);
      setVoiding(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : '取消に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const list = view === 'open' ? openInvoices : monthInvoices;

  // --- 入金の照合(銀行の入金 → 請求書) ---
  const loadMatch = useCallback(async () => {
    setDeposits(null);
    try {
      const from = addDays(today, -MATCH_LOOKBACK_DAYS);
      const [entries, due] = await Promise.all([fetchCashEntriesSince(from), fetchInvoicesDueSince(addDays(from, -90))]);
      setDueInvoices(due);
      setDeposits(entries);
    } catch (e) {
      toast(e instanceof Error ? e.message : '入出金を読み込めませんでした');
      setDeposits([]);
    }
  }, [today, toast]);

  useEffect(() => {
    if (view === 'match' && canConfirm) void loadMatch();
  }, [view, canConfirm, loadMatch]);

  const match = useMemo(() => {
    if (!deposits) return null;
    const all = [...openInvoices, ...dueInvoices.filter((d) => !openInvoices.some((o) => o.id === d.id))];
    const used = usedCashEntryIds(all);
    return proposeMatches(openInvoices, availableDeposits(deposits, used, all));
  }, [deposits, openInvoices, dueInvoices]);

  async function doMatch(targets: MatchProposal[]) {
    if (!user || targets.length === 0) return;
    setBusy(true);
    let done = 0;
    try {
      for (const p of targets) {
        await recordReceipt({
          invoiceId: p.invoice.id,
          date: p.deposit.date,
          amount: p.deposit.amount,
          note: [p.deposit.counterparty, p.deposit.description].filter(Boolean).join(' '),
          userId: user.id,
          cashEntryId: p.deposit.id,
        });
        done++;
      }
      toast(`${done}件を消し込みました`);
    } catch (e) {
      toast(`${done}件を消し込みました。止まりました: ${e instanceof Error ? e.message : '消込に失敗しました'}`);
    } finally {
      setBusy(false);
      await load();
      await loadMatch();
    }
  }

  const actions = (i: Invoice) => (
    <>
      <Link to={`/invoice/${i.id}`} target="_blank" className="txn-link">
        印刷
      </Link>
      {canEdit && i.status !== 'void' && (
        <button
          className="txn-link"
          onClick={() =>
            setReceipt({ invoice: i, date: today, amountText: formatAmountInput(String(Math.max(balanceOf(i), 0))), note: '', refund: false })
          }
        >
          入金を記録
        </button>
      )}
      {canEdit && i.status !== 'void' && i.status !== 'paid' && (
        <button className="txn-link" onClick={() => setDueEdit({ invoice: i, date: i.due_date })}>
          予定日
        </button>
      )}
      {canEdit && i.status !== 'void' && i.receipts.length === 0 && (
        <button className="txn-link" onClick={() => setVoiding({ invoice: i, reason: '' })}>
          取消
        </button>
      )}
    </>
  );

  return (
    <section className="screen invoice-screen">
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          請求書・入金({toMonthLabel(month)})
          {canEdit && (
            <button className="btn-line" style={{ marginLeft: 'auto' }} onClick={() => openCreate()}>
              請求書を作る
            </button>
          )}
        </h3>
        <p className="hint" style={{ margin: '0 0 10px' }}>
          売上は計上済みです。請求書は入金予定(売掛)を作るだけで、売上は増えません。対象月は画面上部の月です。
        </p>

        <div className="queue-summary invoice-tiles">
          <div className="queue-tile" style={{ cursor: 'default' }}>
            <span className="queue-tile-label">売掛残高(未入金の合計)</span>
            <span className="queue-tile-count">¥{formatYen(receivables.open)}</span>
          </div>
          <div className={`queue-tile${receivables.overdueCount > 0 ? ' alert' : ''}`} style={{ cursor: 'default' }}>
            <span className="queue-tile-label">入金予定日を過ぎた分</span>
            <span className="queue-tile-count">¥{formatYen(receivables.overdue)}</span>
            <span className="queue-tile-amount">{receivables.overdueCount}件</span>
          </div>
          <div className="queue-tile" style={{ cursor: 'default' }}>
            <span className="queue-tile-label">この月の未請求の売上</span>
            <span className="queue-tile-count">
              ¥{formatYen(unbilledByCustomer.reduce((s, [, ts]) => s + ts.reduce((x, t) => x + t.amount, 0), 0))}
            </span>
          </div>
        </div>

        <div className="chips">
          {(
            [
              ['month', 'この月の請求書'],
              ['open', '未入金の請求書(全期間)'],
              ['unbilled', 'この月の未請求の売上'],
              ...(canConfirm && canEdit ? ([['match', '入金の照合(銀行)']] as Array<[View, string]>) : []),
            ] as Array<[View, string]>
          ).map(([v, label]) => (
            <button key={v} className={`chip${view === v ? ' on' : ''}`} onClick={() => setView(v)}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="empty">読み込み中…</p>
        ) : view === 'match' ? (
          !match ? (
            <p className="empty">入出金を読み込み中…</p>
          ) : (
            <>
              <p className="hint" style={{ margin: '0 0 8px' }}>
                直近{MATCH_LOOKBACK_DAYS}日の銀行の入金(入出金の「入金・確定」)から、残高とちょうど同じ金額のものを当てています。
                {match.ambiguous > 0 && ` 同じ金額が重なって決められない請求書が${match.ambiguous}件あります(「入金を記録」で選んでください)。`}
                当たらない入金(手数料を引かれた・まとめて振込など)は「入金を記録」で入力してください。
              </p>
              {match.proposals.length === 0 ? (
                <p className="empty">消込の候補はありません</p>
              ) : mobile ? (
                <div className="queue-list">
                  {match.proposals.map((p) => (
                    <div key={p.invoice.id} className="queue-item">
                      <div className="queue-item-main">
                        <span>{p.invoice.customer_name}</span>
                        <span className="num">¥{formatYen(p.deposit.amount)}</span>
                      </div>
                      <div className="queue-item-sub">
                        <span>入金 {toShortDate(p.deposit.date)}</span>
                        <span>{p.deposit.counterparty || p.deposit.description || '—'}</span>
                        <span>{p.invoice.number}(予定 {toShortDate(p.invoice.due_date)})</span>
                      </div>
                      <div className="queue-item-sub">
                        <button className="txn-link" onClick={() => void doMatch([p])} disabled={busy}>
                          消し込む
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <table className="no-stack queue-table">
                  <thead>
                    <tr>
                      <th>入金日</th>
                      <th>振込人・摘要</th>
                      <th className="num">金額</th>
                      <th>請求書</th>
                      <th>取引先</th>
                      <th>入金予定日</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {match.proposals.map((p) => (
                      <tr key={p.invoice.id}>
                        <td>{toShortDate(p.deposit.date)}</td>
                        <td className="queue-payee">{p.deposit.counterparty || p.deposit.description || '—'}</td>
                        <td className="num">{formatYen(p.deposit.amount)}</td>
                        <td>{p.invoice.number}</td>
                        <td className="queue-payee">{p.invoice.customer_name}</td>
                        <td>{toShortDate(p.invoice.due_date)}</td>
                        <td>
                          <button className="txn-link" onClick={() => void doMatch([p])} disabled={busy}>
                            消し込む
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {match.proposals.length > 1 && (
                <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
                  <button className="btn-green" onClick={() => void doMatch(match.proposals)} disabled={busy}>
                    {busy ? '消込中…' : `${match.proposals.length}件をまとめて消し込む`}
                  </button>
                </div>
              )}
            </>
          )
        ) : view === 'unbilled' ? (
          unbilledByCustomer.length === 0 ? (
            <p className="empty">この月の未請求の売上はありません</p>
          ) : (
            <table className={mobile ? undefined : 'no-stack'}>
              <thead>
                <tr>
                  <th>取引先</th>
                  <th className="num">件数</th>
                  <th className="num">金額</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unbilledByCustomer.map(([cid, ts]) => (
                  <tr key={cid ?? '-'}>
                    <td>
                      {cid ? (vendorById.get(cid)?.name ?? cid) : <span className="hint">取引先が未設定(売上・原価入力で取引先を選ぶと請求できます)</span>}
                    </td>
                    <td className="num">{ts.length}</td>
                    <td className="num">{formatYen(ts.reduce((s, t) => s + t.amount, 0))}</td>
                    <td>
                      {cid && canEdit && (
                        <button className="txn-link" onClick={() => openCreate(cid)}>
                          請求書を作る
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : list.length === 0 ? (
          <p className="empty">{view === 'open' ? '未入金の請求書はありません' : 'この月の請求書はありません'}</p>
        ) : mobile ? (
          <div className="queue-list">
            {list.map((i) => (
              <div key={i.id} className="queue-item">
                <div className="queue-item-main">
                  <span>{i.customer_name}</span>
                  <span className="num">¥{formatYen(i.total)}</span>
                </div>
                <div className="queue-item-sub">
                  <span>{i.number}</span>
                  <span className={i.status !== 'paid' && i.status !== 'void' && i.due_date < today ? 'neg' : undefined}>
                    入金予定 {toShortDate(i.due_date)}
                  </span>
                  <span>{INVOICE_STATUS_LABELS[i.status]}</span>
                  <span>残高 ¥{formatYen(balanceOf(i))}</span>
                </div>
                <div className="queue-item-sub" style={{ gap: 14 }}>
                  {actions(i)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="no-stack queue-table">
            <thead>
              <tr>
                <th>番号</th>
                <th>取引先</th>
                <th>対象月</th>
                <th>発行日</th>
                <th className="num">請求額</th>
                <th className="num">入金済み</th>
                <th className="num">残高</th>
                <th>入金予定日</th>
                <th>状態</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.id}>
                  <td>{i.number}</td>
                  <td className="queue-payee">{i.customer_name}</td>
                  <td>{toMonthLabel(i.period_month)}</td>
                  <td>{toShortDate(i.issue_date)}</td>
                  <td className="num">{formatYen(i.total)}</td>
                  <td className="num">{formatYen(i.received)}</td>
                  <td className="num">{formatYen(balanceOf(i))}</td>
                  <td className={i.status !== 'paid' && i.status !== 'void' && i.due_date < today ? 'neg' : undefined}>
                    {toShortDate(i.due_date)}
                  </td>
                  <td>
                    <span className={`badge ${i.status === 'paid' ? 'b-ok' : i.status === 'void' ? 'b-ng' : 'b-pend'}`}>
                      {INVOICE_STATUS_LABELS[i.status]}
                    </span>
                  </td>
                  <td style={{ display: 'flex', gap: 10 }}>{actions(i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {create && (
        <Modal onClose={() => setCreate(null)} width="min(860px, 94vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>請求書を作る({toMonthLabel(month)}分)</h3>
          </div>
          <div className="form-grid">
            <div>
              <label>発行する会社</label>
              <select value={create.companyId} onChange={(e) => setCreate({ ...create, companyId: e.target.value })}>
                {companies
                  .filter((c) => c.active !== false)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label>取引先(売上先・元請け)</label>
              <select
                value={create.customerId}
                onChange={(e) => setCreate({ ...create, customerId: e.target.value, dueDate: '', excluded: new Set() })}
              >
                <option value="">選んでください</option>
                {customers.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}({v.code}){v.active ? '' : '・使用停止'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label>発行日</label>
              <input type="date" value={create.issueDate} onChange={(e) => setCreate({ ...create, issueDate: e.target.value })} />
            </div>
            <div>
              <label>入金予定日(取引先の入金条件から)</label>
              <input
                type="date"
                value={create.dueDate || draft?.due_date || ''}
                onChange={(e) => setCreate({ ...create, dueDate: e.target.value })}
              />
            </div>
          </div>

          {create.customerId && (
            <div style={{ marginTop: 14 }}>
              {createLines.length === 0 ? (
                <p className="empty">この取引先の{toMonthLabel(month)}の未請求の売上はありません</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th />
                      <th>計上日</th>
                      <th>内容</th>
                      <th className="num">金額(税込)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createLines.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <input
                            type="checkbox"
                            style={{ margin: 0, width: 15, height: 15 }}
                            checked={!create.excluded.has(t.id)}
                            onChange={(e) => {
                              const next = new Set(create.excluded);
                              if (e.target.checked) next.delete(t.id);
                              else next.add(t.id);
                              setCreate({ ...create, excluded: next });
                            }}
                          />
                        </td>
                        <td>{toShortDate(t.posting_date)}</td>
                        <td>{describe(t)}</td>
                        <td className="num">{formatYen(t.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {draft && draft.lines.length > 0 && (
                <p style={{ margin: '10px 0 0', textAlign: 'right' }}>
                  請求額 <b>¥{formatYen(draft.total)}</b>(うち消費税 ¥{formatYen(taxIncluded(draft.total))})
                </p>
              )}
            </div>
          )}

          <div className="form-grid" style={{ gridTemplateColumns: '1fr', marginTop: 10 }}>
            <div>
              <label>備考(任意・請求書に載ります)</label>
              <input value={create.note} onChange={(e) => setCreate({ ...create, note: e.target.value })} />
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn-green" onClick={() => void doCreate()} disabled={busy || !draft || draft.lines.length === 0}>
              {busy ? '発行中…' : '発行する'}
            </button>
          </div>
        </Modal>
      )}

      {dueEdit && (
        <Modal onClose={() => setDueEdit(null)} width="min(420px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>入金予定日を直す({dueEdit.invoice.number})</h3>
          </div>
          <div className="form-grid">
            <div>
              <label>入金予定日(発行日 {toShortDate(dueEdit.invoice.issue_date)} 以降)</label>
              <input type="date" value={dueEdit.date} min={dueEdit.invoice.issue_date} onChange={(e) => setDueEdit({ ...dueEdit, date: e.target.value })} />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-green" onClick={() => void doDueEdit()} disabled={busy}>
              {busy ? '保存中…' : '保存する'}
            </button>
          </div>
        </Modal>
      )}

      {receipt && (
        <Modal onClose={() => setReceipt(null)} width="min(520px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>入金を記録({receipt.invoice.number})</h3>
          </div>
          <p className="hint" style={{ margin: '0 0 10px' }}>
            請求額 ¥{formatYen(receipt.invoice.total)} / 入金済み ¥{formatYen(receipt.invoice.received)} / 残高 ¥
            {formatYen(balanceOf(receipt.invoice))}。
          </p>
          <div className="form-grid">
            <div>
              <label>入金日</label>
              <input type="date" value={receipt.date} onChange={(e) => setReceipt({ ...receipt, date: e.target.value })} />
            </div>
            <div>
              <label>金額(円)</label>
              <input
                inputMode="numeric"
                value={receipt.amountText}
                onChange={(e) => {
                  // マイナス(半角・全角・長音記号)を打ったら返金にする
                  const minus = /^\s*[-－−ー‐]/.test(e.target.value);
                  const body = formatAmountInput(e.target.value.replace(/^\s*[-－−ー‐]+/, ''));
                  setReceipt({ ...receipt, amountText: body, refund: minus ? true : receipt.refund });
                }}
              />
            </div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto', cursor: 'pointer', alignSelf: 'end' }}>
              <input
                type="checkbox"
                style={{ margin: 0, width: 15, height: 15 }}
                checked={receipt.refund}
                onChange={(e) => setReceipt({ ...receipt, refund: e.target.checked })}
              />
              返金(マイナスで記録)
            </label>
            <div className="fw">
              <label>メモ(任意)</label>
              <input value={receipt.note} onChange={(e) => setReceipt({ ...receipt, note: e.target.value })} />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-green" onClick={() => void doReceipt()} disabled={busy}>
              {busy ? '記録中…' : receipt.refund ? '返金を記録する' : '記録する'}
            </button>
          </div>
        </Modal>
      )}

      {voiding && (
        <Modal onClose={() => setVoiding(null)} width="min(520px, 92vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12 }}>
            <h3>請求書を取り消す({voiding.invoice.number})</h3>
          </div>
          <p className="hint" style={{ margin: '0 0 10px' }}>
            取り消すと、この請求書の売上はまた「未請求」に戻ります。番号は欠番として残ります。入金の記録がある請求書は取り消せません。
          </p>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div>
              <label>取消の理由</label>
              <input value={voiding.reason} onChange={(e) => setVoiding({ ...voiding, reason: e.target.value })} />
            </div>
          </div>
          <div className="modal-actions">
            <button className="btn-line danger" onClick={() => void doVoid()} disabled={busy || !voiding.reason.trim()}>
              {busy ? '取消中…' : '取り消す'}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
